import { type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { isCountryCode } from './countries';

/**
 * Phone storage policy: when a number can be parsed confidently it is stored
 * in E.164 (`+14155550100`), with any extension appended as ` x<ext>` —
 * a form `parsePhoneNumberFromString` round-trips. When it can't be parsed
 * (no country on the entity, free-form text, short codes), the raw input is
 * stored as-entered. Storage is therefore *mixed by design*; `formatPhone`
 * is the single render-side companion that handles both.
 *
 * Normalization is lenient on purpose: a phone field never blocks a save.
 * The country comes from the owning entity (users.country, contact.country,
 * customer address.country), which since PR #215 is an ISO-3166 alpha-2
 * code — pass it straight through.
 */

/**
 * Normalize raw phone input to stored form.
 *
 * - `''`/whitespace → null ("not provided").
 * - Parses with the entity's country as the default region; input that
 *   carries its own `+` country prefix parses without one.
 * - Valid number → E.164 (+ ` x<ext>` when an extension is present).
 * - Anything else → the trimmed raw input, unchanged.
 */
export function toStoredPhone(
  raw: string | null | undefined,
  country?: string | null,
): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const region = country && isCountryCode(country) ? (country as CountryCode) : undefined;
  const parsed = parsePhoneNumberFromString(trimmed, region);
  if (!parsed?.isValid()) return trimmed;
  return parsed.ext ? `${parsed.number} x${parsed.ext}` : parsed.number;
}

/**
 * Display form for a stored phone: the ITU E.123 international format —
 * country code plus the number's own national digit grouping, no national
 * punctuation ("+1 415 555 0100", "+91 98765 43210"). Raw verbatim for
 * legacy/unparseable values, so it's safe to call unconditionally at
 * render sites.
 */
export function formatPhone(stored: string | null | undefined): string {
  const trimmed = stored?.trim();
  if (!trimmed) return '';
  const parsed = parsePhoneNumberFromString(trimmed);
  if (!parsed?.isValid()) return trimmed;
  return parsed.formatInternational();
}

/** Strip everything but digits — the comparison key for phone search. */
export function phoneDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Patch fragment re-normalizing stored phones after the owning entity's
 * country changed. The single home for that policy — every domain service
 * (users, contacts, customers — patch and import-update alike) applies it
 * rather than hand-rolling the loop.
 *
 * For each `field` the caller's input did NOT touch (undefined in
 * `touched`), re-runs {@link toStoredPhone} against the new country and
 * includes the field only when the stored value actually changes: E.164
 * values are a no-op (they parse without a region), but a raw national
 * number saved before the entity had a country can now resolve.
 */
export function renormalizePhones<K extends string>(
  prior: Readonly<Partial<Record<K, string | null>>>,
  touched: Readonly<Partial<Record<K, unknown>>>,
  fields: readonly K[],
  country: string | null | undefined,
): Partial<Record<K, string | null>> {
  const patch: Partial<Record<K, string | null>> = {};
  for (const field of fields) {
    if (touched[field] !== undefined) continue;
    const renormalized = toStoredPhone(prior[field], country);
    if (renormalized !== (prior[field] ?? null)) patch[field] = renormalized;
  }
  return patch;
}
